import { landscapeInitials, landscapeLogo } from '@protoss/shared';
import { BarChartIcon, SkeletonBlock } from '@protoss/ui';
import * as React from 'react';
import './vendor-card.css';

// ─── VendorLogo ──────────────────────────────────────────────────────────────

/** Products whose bundled logo key is neither the bare product key nor the vendor key. */
const LOGO_KEY_BY_PRODUCT: Readonly<Record<string, string>> = {
  'sage:intacct': 'sage_intacct',
};

/**
 * VendorLogo - resolves a branded icon for a known vendor+product combination,
 * falling back to two-letter initials for unknown entries.
 * Logos come from the systems-landscape bundle in @protoss/shared (sources and usage notes in
 * packages/shared/src/system-landscape/logos/SOURCES.md); NetSuite and Rillet keep their larger PNGs.
 */
export function VendorLogo({
  vendorKey,
  productKey,
  productName,
  size = 24,
}: {
  vendorKey: string;
  productKey: string;
  productName: string;
  size?: number;
}) {
  if (vendorKey === 'oracle' && (productKey === 'netsuite' || productKey === 'oracle:netsuite')) {
    return (
      <div className="vendor-card__logo-container vendor-card__logo-container--netsuite">
        <img
          src="/products/oracle-netsuite/logo.png"
          alt={`${productName} logo`}
          width={size}
          height={size}
          className="vendor-card__logo-img vendor-card__logo-img--netsuite"
        />
      </div>
    );
  }
  if (vendorKey === 'rillet' && (productKey === 'rillet' || productKey === 'rillet:rillet')) {
    return (
      <div className="vendor-card__logo-container vendor-card__logo-container--rillet">
        <img
          src="/products/rillet/logo.png"
          alt={`${productName} logo`}
          width="100%"
          height="100%"
          className="vendor-card__logo-img vendor-card__logo-img--rillet"
        />
      </div>
    );
  }
  // Maxio's bundled logo is a wide wordmark; the card uses its square "mx" mark, cropped from it.
  if (vendorKey === 'maxio' && (productKey === 'maxio' || productKey === 'maxio:maxio')) {
    return (
      <img
        src="/products/maxio/logo.svg"
        alt={`${productName} logo`}
        width={size}
        height={size}
        className="vendor-card__logo-img vendor-card__logo-img--brand vendor-card__logo-img--maxio"
      />
    );
  }
  // Every other product: the vendor logo bundled for the systems diagram (Salesforce, Sage Intacct,
  // SAP, ...), keyed by the diagram's logo key, the bare product key, then the vendor key (so
  // `salesforce:sales-cloud` gets Salesforce's). Dynamics 365 has no bundled logo yet, so initials.
  const bareProductKey = productKey.includes(':') ? productKey.split(':')[1]! : productKey;
  const logo =
    landscapeLogo(LOGO_KEY_BY_PRODUCT[productKey]) ??
    landscapeLogo(bareProductKey) ??
    landscapeLogo(vendorKey);
  if (logo) {
    return (
      <img
        src={logo}
        alt={`${productName} logo`}
        width={size}
        height={size}
        className="vendor-card__logo-img vendor-card__logo-img--brand"
      />
    );
  }
  if (bareProductKey === 'data-platform') {
    return (
      <BarChartIcon className="vendor-card__logo-icon" width={size} height={size} aria-hidden />
    );
  }
  return <span className="vendor-card__logo-letter">{landscapeInitials(productName)}</span>;
}

// ─── VendorCard ──────────────────────────────────────────────────────────────

export interface VendorCardProps {
  logo: React.ReactNode;
  name: string;
  /** Secondary line below the name (vendor name, product name, etc.) */
  subtitle?: string;
  /** Optional badge(s) rendered below the name, e.g. status pills. */
  badges?: React.ReactNode;
  isSelected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  /** Set true when the logo asset has its own background (no muted fill needed). */
  logoTransparent?: boolean;
}

export function VendorCard({
  logo,
  name,
  subtitle,
  badges,
  isSelected,
  onClick,
  disabled,
  logoTransparent,
}: VendorCardProps) {
  return (
    <button
      type="button"
      className={`vendor-card${isSelected ? ' is-selected' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span
        className={`vendor-card__logo${logoTransparent ? ' vendor-card__logo--transparent' : ''}`}
      >
        {logo}
      </span>
      <span className="vendor-card__body">
        <span className="vendor-card__name">{name}</span>
        {subtitle != null && <span className="vendor-card__subtitle">{subtitle}</span>}
        {badges != null && <span className="vendor-card__badges">{badges}</span>}
      </span>
    </button>
  );
}

// ─── VendorCardSkeleton ───────────────────────────────────────────────────────

export function VendorCardSkeleton({ badges = false }: { badges?: boolean }) {
  return (
    <div className="vendor-card vendor-card--skeleton" aria-hidden="true">
      <span className="vendor-card__logo">
        <SkeletonBlock />
      </span>
      <span className="vendor-card__body">
        <span className="vendor-card__skeleton-name">
          <SkeletonBlock />
        </span>
        {badges && (
          <span className="vendor-card__skeleton-badge">
            <SkeletonBlock />
          </span>
        )}
      </span>
    </div>
  );
}
